import { createEffect, createSignal, on, Show, onCleanup, onMount, Match, Switch, useContext } from 'solid-js';
import clsx from 'clsx';
import { getYoutubeVideoId, getVimeoUrl } from '../../components/video-embed/helpers';
import { VideoImageBeforeCookiesAccepted } from '../../components/video-embed/video-image-before-cookie';
import { CookieModal } from '../cookie-modal/cookie-modal';
import { TabHandler } from '../../tools/tab-handler';
import { AppContext } from '../../app-context-provider/app-context-provider';

type VideoIframeContainerProps = {
    videoLink?: string;
};


export const VideoIframeContainer = (props: VideoIframeContainerProps) => {
    const { localize } = useContext(AppContext);

    const [ isLoading, setIsLoading ] = createSignal(false);
    const [showModal, setShowModal] = createSignal(false);
    const [autoplayVideo, setAutoplayVideo] = createSignal(false);
    const [videoCookiesAccepted, setVideoCookiesAccepted] = createSignal(false);

    const [ firstTabbableElement, setFirstTabbableElement ] = createSignal<HTMLElement | undefined>();
    const [ lastTabbableElement, setLastTabbableElement ] = createSignal<HTMLElement | undefined>();

    createEffect(() => {
        setIsLoading(true);
    });

    createEffect(
        on(
            () => props.videoLink,
            () => {
                setIsLoading(false);
                setTimeout(() => {
                    setIsLoading(true);
                }, 10);
            }
        )
    );

    onMount(() => {
        checkConsent();

        window.addEventListener('CookiebotOnAccept', () => {
            checkConsent();
        });

        onCleanup(() => {
            window.removeEventListener('CookiebotOnAccept', checkConsent);
        });
    });

    const checkConsent = () => {
        if (window?.Cookiebot?.consent?.marketing) {
            setVideoCookiesAccepted(true);
        }
    };

    const handleThumbnailClick = () => {
        setAutoplayVideo(true);
        setShowModal(true);
    };

    return (
        <Show when={props.videoLink}>
            <button
                onClick={() => !videoCookiesAccepted() ? handleThumbnailClick() : null}
                aria-label={localize('open-video', 'Open video')}
                class={clsx(
                    'flex justify-center w-full my-[2.5rem] relative cursor-pointer',
                    'animate-fade-in-delayed',
                    '[&>iframe]:w-full [&>iframe]:h-min-content [&>iframe]:aspect-video [&>iframe]:rounded-[1.2rem] max-mobile:[&>iframe]:rounded-[0.7rem]',
                    !videoCookiesAccepted() && 'pb-[56.25%]',
                    !isLoading() && 'animate-[fade-out_0.25s_ease-in-out]'
                )}
            >
                <Switch>
                    <Match when={videoCookiesAccepted()}>
                        <Show when={props.videoLink?.includes('youtu')}>
                            <iframe
                                src={`https://www.youtube-nocookie.com/embed/${getYoutubeVideoId(props.videoLink!)}?autoplay=${autoplayVideo() ? 1 : 0}&mute=${autoplayVideo() ? 1 : 0}`}
                                title="YouTube video player"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowfullscreen
                                referrerpolicy="strict-origin-when-cross-origin"
                            ></iframe>
                        </Show>
                        <Show when={props.videoLink?.includes('vimeo')}>
                            <iframe
                                src={getVimeoUrl(props.videoLink!)}
                                allow="autoplay; fullscreen; picture-in-picture"
                                allowfullscreen
                                referrerpolicy="strict-origin-when-cross-origin"
                            ></iframe>
                        </Show>
                    </Match>
                    <Match when={!videoCookiesAccepted()}>
                        <VideoImageBeforeCookiesAccepted
                            videoUrl={props.videoLink!}
                        />
                    </Match>
                </Switch>

                <Show when={showModal()}>
                    <TabHandler refs={{
                        firstTabbableElement,
                        lastTabbableElement,
                    }}>
                        <CookieModal
                            close={() => setShowModal(false)}
                            setFirstTabbableElement={(el: HTMLElement) => setFirstTabbableElement(el)}
                            setLastTabbableElement={(el: HTMLElement) => setLastTabbableElement(el)}
                        />
                    </TabHandler>
                </Show>

            </button>
        </Show>
    );
};
